'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Logo } from '@/components/layout/Logo';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { TextField } from '@/components/ui/Field';
import { Alert, Spinner } from '@/components/ui/Feedback';
import { Icon } from '@/components/ui/Icon';
import { Container } from '@/components/ui/Section';
import { TABLET_PORTAL_URL } from '@/config/env';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/hooks/useAuth';
import { hasErrors, isBlank, messages, type ValidationErrors } from '@/lib/validation';

interface LoginValues {
  identifier: string;
  password: string;
}

const PERKS = [
  'Manage every LaunchPad from one dashboard',
  'Swap the ad and promotion on screen remotely',
  'Track signups and commissions by location',
  'Restart a device or grab a screenshot without a site visit',
];

export function LoginForm() {
  const [values, setValues] = useState<LoginValues>({ identifier: '', password: '' });
  const [errors, setErrors] = useState<ValidationErrors<LoginValues>>({});
  const [showPassword, setShowPassword] = useState(false);

  const { signIn, isSubmitting, errorMessage, clearError, isAuthenticated } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (isAuthenticated) router.replace(ROUTES.dashboard);
  }, [isAuthenticated, router]);

  const setValue = (key: keyof LoginValues, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    clearError();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: ValidationErrors<LoginValues> = {
      identifier: isBlank(values.identifier) ? messages.required : undefined,
      password: isBlank(values.password) ? messages.required : undefined,
    };
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) return;

    const ok = await signIn({ identifier: values.identifier.trim(), password: values.password });
    if (ok) router.replace(ROUTES.dashboard);
  };

  return (
    <section className="relative overflow-hidden py-14 sm:py-20">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:linear-gradient(to_right,#2e9bff_1px,transparent_1px),linear-gradient(to_bottom,#2e9bff_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_at_50%_0%,black,transparent_70%)]"
      />

      <Container className="relative">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div className="hidden flex-col gap-8 lg:flex">
            <Logo />
            <h1 className="text-4xl leading-[1] xl:text-5xl">
              Sign in to your
              <span className="text-gradient-brand block">LaunchPad dashboard</span>
            </h1>
            <p className="text-content-muted max-w-md text-lg">
              Same account as the tablet app — you no longer need to navigate to spk2go.mobi to manage your devices.
            </p>
            <ul className="flex flex-col gap-3">
              {PERKS.map((perk) => (
                <li key={perk} className="text-content-muted flex items-start gap-3 text-sm">
                  <Icon name="check" className="text-brand-bright mt-0.5 h-5 w-5" />
                  {perk}
                </li>
              ))}
            </ul>
          </div>

          <Card className="mx-auto w-full max-w-md" highlighted>
            <div className="mb-8 flex flex-col gap-2 lg:hidden">
              <Logo />
            </div>

            <h2 className="text-2xl text-white">Operator login</h2>
            <p className="text-content-muted mt-2 text-sm">Use the same email and password you use for the tablet app.</p>

            <form onSubmit={handleSubmit} noValidate className="mt-8 flex flex-col gap-5">
              <TextField
                label="Email or username"
                required
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={values.identifier}
                error={errors.identifier}
                onChange={(event) => setValue('identifier', event.target.value)}
              />

              <div className="relative">
                <TextField
                  label="Password"
                  required
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={values.password}
                  error={errors.password}
                  onChange={(event) => setValue('password', event.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-pressed={showPassword}
                  className="text-content-subtle hover:text-content absolute top-8 right-2 inline-flex h-10 items-center rounded-lg px-3 text-xs font-semibold tracking-wider uppercase transition"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>

              {errorMessage ? <Alert tone="error">{errorMessage}</Alert> : null}

              <Button type="submit" size="lg" fullWidth disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner /> Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <Icon name="arrow" className="h-5 w-5" />
                  </>
                )}
              </Button>
            </form>

            <div className="border-line/70 mt-8 flex flex-col gap-3 border-t pt-6 text-sm">
              <a
                href={TABLET_PORTAL_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="text-content-muted hover:text-brand-bright inline-flex items-center gap-2 transition"
              >
                <Icon name="link" className="h-4 w-4" />
                Open the SPK2GO portal instead
              </a>
              <p className="text-content-subtle">
                No account yet?{' '}
                <Link href={ROUTES.buy} className="text-brand-bright font-medium">
                  Get a LaunchPad
                </Link>{' '}
                and we will set one up for you.
              </p>
            </div>
          </Card>
        </div>
      </Container>
    </section>
  );
}
